import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sumoPrimary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.98]",
  {
    variants: {
      variant: {
        default:
          "bg-sumoNavy text-white shadow-soft hover:shadow-medium hover:bg-sumoNavy/90",
        primary:
          "bg-sumoPrimary text-sumoNavy shadow-yellow hover:shadow-strong hover:bg-sumoAmber font-bold",
        primaryGlow:
          "bg-sumoPrimary text-sumoNavy shadow-lg shadow-sumoPrimary/15 hover:shadow-xl hover:shadow-sumoPrimary/25 hover:bg-sumoAmber font-bold transition-all duration-300",
        destructive:
          "bg-error500 text-white shadow-soft hover:shadow-medium hover:bg-error700",
        outline:
          "border-2 border-neutral300 bg-white text-neutral900 shadow-soft hover:shadow-medium hover:border-sumoPrimary hover:text-sumoPrimary",
        outlinePrimary:
          "border-2 border-sumoPrimary bg-white text-sumoPrimary shadow-soft hover:bg-sumoPrimary hover:text-sumoNavy",
        secondary:
          "bg-neutral100 text-neutral900 shadow-soft hover:bg-neutral200 hover:shadow-medium",
        ghost: "hover:bg-neutral100 hover:text-neutral900",
        link: "text-sumoPrimary underline-offset-4 hover:underline hover:text-sumoAmber",
        success: "bg-success500 text-white shadow-soft hover:shadow-medium hover:bg-success700",
        teal: "bg-sumoTeal text-white shadow-soft hover:shadow-medium hover:bg-sumoTeal/90",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-12 rounded-lg px-8 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
